import {
    getCart,
    addToCart as addToCartApi,
    updateCartItem,
    removeCartItem,
    clearCart as clearCartApi,
} from "../api/cart.api";
import {
    applyCoupon as applyCouponApi,
    removeCoupon as removeCouponApi,
} from "../api/coupon.api";

// NOTE: createContext, useState, useEffect, useCallback, useMemo are auto-imported by unplugin-auto-import

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState(null);
    const [cartLoading, setCartLoading] = useState(true);
    const [cartItemCount, setCartItemCount] = useState(0);

    const updateCartItemCount = useCallback((cartData) => {
        if (!cartData || !cartData.items) {
            setCartItemCount(0);
            return;
        }
        const count = cartData.items.reduce(
            (total, item) => total + (item.quantity || 1),
            0
        );
        setCartItemCount(count);
    }, []);

    const fetchCart = useCallback(async () => {
        try {
            setCartLoading(true);
            const response = await getCart();
            const cartData = response.data.data || response.data;
            setCart(cartData);
            updateCartItemCount(cartData);
        } catch {
            setCart(null);
            setCartItemCount(0);
        } finally {
            setCartLoading(false);
        }
    }, [updateCartItemCount]);

    const addToCart = useCallback(
        async (variantId, quantity) => {
            const response = await addToCartApi({ variantId, quantity });
            const cartData = response.data.data || response.data;
            setCart(cartData);
            updateCartItemCount(cartData);
            return response;
        },
        [updateCartItemCount]
    );

    const updateQuantity = useCallback(
        async (variantId, quantity) => {
            const response = await updateCartItem(variantId, { quantity });
            const cartData = response.data.data || response.data;
            setCart(cartData);
            updateCartItemCount(cartData);
            return response;
        },
        [updateCartItemCount]
    );

    const removeItem = useCallback(
        async (variantId) => {
            const response = await removeCartItem(variantId);
            const cartData = response.data.data || response.data;
            setCart(cartData);
            updateCartItemCount(cartData);
            return response;
        },
        [updateCartItemCount]
    );

    const clearCartItems = useCallback(async () => {
        const response = await clearCartApi();
        setCart(null);
        setCartItemCount(0);
        return response;
    }, []);

    const applyCoupon = useCallback(
        async (code) => {
            const response = await applyCouponApi(code);
            const cartData = response.data.data || response.data;
            setCart(cartData);
            updateCartItemCount(cartData);
            return response;
        },
        [updateCartItemCount]
    );

    const removeCoupon = useCallback(
        async () => {
            const response = await removeCouponApi();
            const cartData = response.data.data || response.data;
            setCart(cartData);
            updateCartItemCount(cartData);
            return response;
        },
        [updateCartItemCount]
    );

    const refreshCart = useCallback(() => {
        return fetchCart();
    }, [fetchCart]);

    // Fetch cart on mount
    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const value = useMemo(
        () => ({
            cart,
            cartLoading,
            cartItemCount,
            addToCart,
            updateQuantity,
            removeItem,
            clearCartItems,
            applyCoupon,
            removeCoupon,
            refreshCart,
        }),
        [cart, cartLoading, cartItemCount, addToCart, updateQuantity, removeItem, clearCartItems, applyCoupon, removeCoupon, refreshCart]
    );

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export const useCartContext = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCartContext must be used within a CartProvider');
    }
    return context;
};

export { CartContext };
export default CartContext;
